'use client';

import React, { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import type { Form } from '../../payload-types';
import { registerInlineCalendlyFrame } from '../../utilities/calendly/booking-owner';
import { FormPrivacyPolicyLink } from './form-privacy-policy-link';

// Lazy-load `react-calendly` so the ~20KB package only ships on routes
// that actually render a Calendly embed.
const InlineWidget = dynamic(
  () => import('react-calendly').then((m) => ({ default: m.InlineWidget })),
  { ssr: false },
);

const CalendlyTracker = dynamic(() => import('./calendly-tracker'), { ssr: false });

/**
 * A booking Form's Calendly Booking Page, inline. `url` is the Form's
 * Booking Page (`resolveBookingPage`).
 */
const CalendlyEmbed: React.FC<{
  form: Form;
  url: string;
}> = ({ form, url }) => {
  const widgetRef = useRef<HTMLDivElement>(null);

  // A Booking made in this widget credits this Form alone, however many
  // booking Forms the page holds: its iframe's window is what Calendly's
  // messages come from (`booking-owner.ts`). Read when a message arrives, as
  // the widget loads after this effect runs.
  useEffect(
    () => registerInlineCalendlyFrame(form.id, () => widgetRef.current?.querySelector('iframe')?.contentWindow),
    [form.id],
  );

  return (
    <>
      <div ref={widgetRef}>
        <InlineWidget
          url={url}
          styles={{ minWidth: '320px', height: '700px' }}
        />
      </div>
      {/* The booking widget collects the visitor's details, so the form's policy sits under it too. */}
      <FormPrivacyPolicyLink policy={form.privacyPolicy} className="mt-3" />
      <CalendlyTracker form={form} />
    </>
  );
};

export default CalendlyEmbed;
